// Horaires de travail, tolérance, jours travaillés, droit aux congés, jours fériés.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, FormControlLabel, Grid, IconButton, MenuItem, Snackbar, Table,
  TableBody, TableCell, TableRow, TextField, Typography,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import Layout from '../../components/Layout';
import { rhAPI } from '../../services/api';
import { apiError, dateFr } from '../../utils/format';
import { JOURS } from '../../utils/rh';

const RhParametres = () => {
  const [p, setP] = useState(null);
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [feries, setFeries] = useState([]);
  const [nouveau, setNouveau] = useState({ date: '', libelle: '' });
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => {
    rhAPI.params().then((r) => setP({ ...r.data, jours: r.data.jours_travailles.split(',').map(Number) })).catch((e) => setError(apiError(e)));
  }, []);
  const loadFeries = useCallback(() => { rhAPI.feries(annee).then((r) => setFeries(r.data)).catch((e) => setError(apiError(e))); }, [annee]);
  useEffect(() => { loadFeries(); }, [loadFeries]);

  const save = async () => {
    setError('');
    try {
      await rhAPI.saveParams({ ...p, jours_travailles: p.jours });
      setToast('Paramètres enregistrés');
    } catch (e) { setError(apiError(e)); }
  };
  const addFerie = async () => {
    try { await rhAPI.addFerie(nouveau); setNouveau({ date: '', libelle: '' }); setToast('Jour férié ajouté'); loadFeries(); } catch (e) { setError(apiError(e)); }
  };
  const delFerie = async (d) => {
    try { await rhAPI.deleteFerie(d); loadFeries(); } catch (e) { setError(apiError(e)); }
  };
  const toggleDay = (n) => setP({ ...p, jours: p.jours.includes(n) ? p.jours.filter((x) => x !== n) : [...p.jours, n].sort() });

  return (
    <Layout title="Paramètres RH" plain>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Horaires de travail</Typography>
              {p && (
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField type="time" label="Début" value={p.heure_debut} InputLabelProps={{ shrink: true }} onChange={(e) => setP({ ...p, heure_debut: e.target.value })} fullWidth />
                    <TextField type="time" label="Fin" value={p.heure_fin} InputLabelProps={{ shrink: true }} onChange={(e) => setP({ ...p, heure_fin: e.target.value })} fullWidth />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField type="number" label="Tolérance de retard (min)" value={p.tolerance_minutes} fullWidth
                      helperText="Au-delà, l'arrivée compte comme un retard" onChange={(e) => setP({ ...p, tolerance_minutes: Number(e.target.value) })} />
                    <TextField type="number" label="Pause déjeuner (min)" value={p.pause_minutes} fullWidth
                      helperText="Déduite des heures travaillées" onChange={(e) => setP({ ...p, pause_minutes: Number(e.target.value) })} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Jours travaillés</Typography>
                    {JOURS.map((j, i) => (
                      <FormControlLabel key={j} label={j} control={<Checkbox checked={p.jours.includes(i + 1)} onChange={() => toggleDay(i + 1)} />} />
                    ))}
                  </Box>
                  <TextField label="Droit annuel aux congés (jours ouvrés)" value={p.conges_annuels} inputMode="decimal"
                    helperText="Code du travail : 2 jours ouvrables par mois de service, soit 24 jours"
                    onChange={(e) => setP({ ...p, conges_annuels: e.target.value })} />
                  <Box><Button variant="contained" onClick={save}>Enregistrer</Button></Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>Jours fériés</Typography>
                <TextField select size="small" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} inputProps={{ 'aria-label': 'Année' }}>
                  {[annee - 1, annee, annee + 1].map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
              </Box>
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Les fêtes religieuses à date variable (Korité, Tabaski, Tamkharit, Maouloud, Magal…) sont à ajouter chaque année selon les dates officielles.
              </Alert>
              <Table size="small">
                <TableBody>
                  {feries.map((f) => (
                    <TableRow key={f.date}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateFr(f.date)}</TableCell>
                      <TableCell>{f.libelle}</TableCell>
                      <TableCell padding="checkbox">
                        <IconButton size="small" aria-label={`Supprimer ${f.libelle}`} onClick={() => delFerie(f.date)}><DeleteOutline fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {feries.length === 0 && <TableRow><TableCell colSpan={3}>Aucun jour férié saisi pour {annee}</TableCell></TableRow>}
                </TableBody>
              </Table>
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <TextField type="date" size="small" label="Date" value={nouveau.date} InputLabelProps={{ shrink: true }} onChange={(e) => setNouveau({ ...nouveau, date: e.target.value })} />
                <TextField size="small" label="Libellé" value={nouveau.libelle} onChange={(e) => setNouveau({ ...nouveau, libelle: e.target.value })} sx={{ flex: 1 }} />
                <Button variant="outlined" disabled={!nouveau.date || !nouveau.libelle} onClick={addFerie}>Ajouter</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
};

export default RhParametres;
